import Layout from "@/components/layout/Layout";
import Button from "@/components/ui/Button";
import { JSX, useEffect, useState } from "react";
import { FaPlus } from "react-icons/fa";
import { BiTrash } from "react-icons/bi";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAdCategoryStore } from "./store/adCategoryStore";
import { useShallow } from "zustand/react/shallow";
import useGlobalStore from "@/store/globalStore";
import Spinner from "@/components/ui/Spinner";
import PopoverMenu from "@/components/ui/Popeover";
import { Edit, Eye, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/Dialog";

export default function ManageAdCategoriesPublishers(): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [publisherToDelete, setPublisherToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [adTypeToDelete, setAdTypeToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [sortField, setSortField] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  const navigate = useNavigate();
  const {
    getAdCategoryDetailData,
    adCategoryDetailData,
    setEditData,
    deleteAdCategoryPublisher,
    deleteAdCategoryAdType,
  } = useAdCategoryStore(
    useShallow((state) => ({
      getAdCategoryDetailData: state.getAdCategoryDetailData,
      adCategoryDetailData: state.adCategoryDetailData,
      setEditData: state.setEditData,
      deleteAdCategoryPublisher: state.deleteAdCategoryPublisher,
      deleteAdCategoryAdType: state.deleteAdCategoryAdType,
    })),
  );

  const setError = useGlobalStore((state) => state.setError);
  const showSuccess = useGlobalStore((state) => state.showSuccess);

  const [searchParams] = useSearchParams();

  const fetchData = async () => {
    try {
      await getAdCategoryDetailData(searchParams.get("id"));
    } catch (error) {
      setError(error.message ?? "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!publisherToDelete) return;

    try {
      await deleteAdCategoryPublisher(publisherToDelete.id);
      await fetchData();
      showSuccess("Publisher archived successfully");
      setPublisherToDelete(null);
    } catch (error) {
      setError(error.message ?? "Failed to delete publisher");
    }
  };

  const handleDeleteAdType = async () => {
    if (!adTypeToDelete) return;

    try {
      await deleteAdCategoryAdType(adTypeToDelete.id);
      await fetchData();
      showSuccess("Ad type archived successfully");
      setAdTypeToDelete(null);
    } catch (error) {
      setError(error.message ?? "Failed to delete ad type");
    }
  };

  const createMenuItems = (
    adCategoryId: string,
    publisherId: string,
    publisherName: string,
    adInputName: string,
    adInputId: string,
  ) => [
    {
      label: "Create New Ad Type For This Publisher",
      icon: <FaPlus className="w-4 h-4" />,
      onClick: async () => {
        await getAdCategoryDetailData(adCategoryId);
        const detailData = useAdCategoryStore.getState().adCategoryDetailData;
        if (detailData) {
          setEditData(detailData);
        }
        navigate(`/create-ad-categories?id=${adCategoryId}&step=3`);
      },
      showDivider: true,
    },
    {
      label: "View Ad Type",
      icon: <Eye className="w-4 h-4" />,
      onClick: () =>
        navigate(
          `/ad-categories/view-ad-type?id=${adCategoryId}&publisherId=${publisherId}&adInputId=${adInputId}`,
        ),
      showDivider: true,
    },
    {
      label: "Edit Publisher",
      icon: <Edit className="w-4 h-4" />,
      onClick: async () => {
        await getAdCategoryDetailData(adCategoryId);
        const detailData = useAdCategoryStore.getState().adCategoryDetailData;
        if (detailData) {
          setEditData(detailData);
        }
        navigate(`/create-ad-categories?id=${adCategoryId}&step=2`);
      },
    },
    {
      label: "Edit Ad Type",
      icon: <Edit className="w-4 h-4" />,
      onClick: async () => {
        await getAdCategoryDetailData(adCategoryId);
        const detailData = useAdCategoryStore.getState().adCategoryDetailData;
        if (detailData) {
          setEditData(detailData);
        }
        navigate(`/create-ad-categories?id=${adCategoryId}&step=3`);
      },
    },
    {
      label: "Edit Ad Type Parameters",
      icon: <Edit className="w-4 h-4" />,
      onClick: async () => {
        await getAdCategoryDetailData(adCategoryId);
        const detailData = useAdCategoryStore.getState().adCategoryDetailData;
        if (detailData) {
          setEditData(detailData);
        }
        const adTypes = useAdCategoryStore.getState().adTypes;
        const adTypeIndex = adTypes.findIndex(
          (at) => at.publisherId === publisherId,
        );
        const adInputIndex =
          adTypeIndex >= 0
            ? adTypes[adTypeIndex].adInputs.findIndex(
                (ai) => ai.name === adInputName,
              )
            : 0;
        navigate(
          `/create-ad-categories?id=${adCategoryId}&step=4&viewAdType=true&adTypeIndex=${adTypeIndex >= 0 ? adTypeIndex : 0}&adInputIndex=${adInputIndex >= 0 ? adInputIndex : 0}`,
        );
      },
    },
    {
      label: "Edit Ad Type Template",
      icon: <Edit className="w-4 h-4" />,
      onClick: () => {
        navigate(`/create-ad-categories?id=${adCategoryId}&step=5&adInputId=${adInputId}`);
      },
      showDivider: true,
    },
    {
      label: "Archive Publisher",
      icon: <Trash2 className="w-4 h-4" />,
      onClick: () =>
        setPublisherToDelete({ id: publisherId, name: publisherName }),
    },
    {
      label: "Archive Ad Type",
      icon: <Trash2 className="w-4 h-4" />,
      onClick: () => {
        if (adInputId) {
          setAdTypeToDelete({ id: adInputId, name: adInputName });
        }
      },
    },
  ];

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) {
      return (
        <svg
          className="w-4 h-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      );
    }
    return (
      <svg
        className={`w-4 h-4 text-pink-500 transition-transform ${
          sortOrder === "desc" ? "rotate-180" : ""
        }`}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        viewBox="0 0 24 24"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    );
  };

  const goPrevious = () => {
    setCurrentPage((page) => Math.max(page - 1, 1));
  };

  const goNext = () => {
    setCurrentPage((page) => Math.min(page + 1, totalPages));
  };

  // Flatten ad_types into one row per publisher + ad input combination
  const flatRows = adCategoryDetailData?.ad_types
    ? adCategoryDetailData.ad_types.flatMap((adType) =>
        adType.ad_inputs.length > 0
          ? adType.ad_inputs.map((adInput) => {
              const params = adInput.parameters ?? [];
              const customParams = adInput.custom_parameters ?? [];

              const activeParameters =
                params.filter((p) => p.is_enabled !== false).length +
                customParams.filter((p: any) => p?.enabled !== false).length;

              const inactiveParameters =
                params.filter((p) => p.is_enabled === false).length +
                customParams.filter((p: any) => p?.enabled === false).length;

              // Pull placement info value if present
              const placementParam = params.find((p) =>
                p.parameter_definition?.key
                  ?.toLowerCase()
                  .includes("placement"),
              );
              const placementInfo =
                (placementParam?.inputs_json &&
                  (placementParam.inputs_json.placement_info ??
                    Object.values(placementParam.inputs_json)[0])) ||
                "-";

              return {
                publisherName: adType.publisher_details.name,
                publisherId: adType.publisher_details.id,
                adInputName: adInput.name,
                adInputId: adInput.id,
                mediaType: adInput.media_type,
                activeParameters,
                inactiveParameters,
                placementInfo,
                createdAt: adInput.created_at ?? null,
              };
            })
          : [
              {
                publisherName: adType.publisher_details.name,
                publisherId: adType.publisher_details.id,
                adInputName: "-",
                adInputId: "",
                mediaType: "-",
                activeParameters: 0,
                inactiveParameters: 0,
                placementInfo: "-",
                createdAt: null,
              },
            ],
      )
    : [];

  const sortedRows = [...flatRows].sort((a, b) => {
    switch (sortField) {
      case "name":
        return sortOrder === "asc"
          ? a.publisherName.toLowerCase().localeCompare(b.publisherName.toLowerCase())
          : b.publisherName.toLowerCase().localeCompare(a.publisherName.toLowerCase());
      case "ad_type":
        return sortOrder === "asc"
          ? a.adInputName.toLowerCase().localeCompare(b.adInputName.toLowerCase())
          : b.adInputName.toLowerCase().localeCompare(a.adInputName.toLowerCase());
      case "active_parameters":
        return sortOrder === "asc"
          ? a.activeParameters - b.activeParameters
          : b.activeParameters - a.activeParameters;
      case "inactive_parameters":
        return sortOrder === "asc"
          ? a.inactiveParameters - b.inactiveParameters
          : b.inactiveParameters - a.inactiveParameters;
      case "date_created": {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return sortOrder === "asc" ? aTime - bTime : bTime - aTime;
      }
      default:
        return 0;
    }
  });

  const totalPages = Math.ceil(sortedRows.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentRows = sortedRows.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    fetchData();
  }, []);

  if (!searchParams.get("id")) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          Oops!!! The Publisher you are looking is not found.
        </div>
      </Layout>
    );
  }

  if (isLoading) {
    return <Spinner className="flex justify-centers" />;
  }

  return (
    <Layout>
      <div>
        <div className="flex justify-between items-start mb-4">
          <h1 className="text-2xl font-bold">
            {adCategoryDetailData?.name || "Ad Category"}
          </h1>
          <div className="flex flex-col items-end gap-2">
            <Button
              type="text"
              className="text-gray-500 hover:text-gray-700 underline text-sm"
              onClick={() => {
                navigate(`/ad-categories/archived-publishers?id=${searchParams.get("id")}`);
              }}
            >
              View Archived Publishers
            </Button>
            <Button
              type="text"
              className="text-gray-500 hover:text-gray-700 underline text-sm"
              onClick={() => {
                navigate(`/ad-categories/archived-ad-types?id=${searchParams.get("id")}`);
              }}
            >
              View Archived Ad Types
            </Button>
          </div>
        </div>

        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2 text-sm">
            <button
              onClick={() => navigate("/ad-categories")}
              className="text-pink-500 hover:text-pink-700 text-2xl"
              aria-label="Back to Ad Categories Dashboard"
            >
              ←
            </button>
            <span className="text-black text-xl">Ad Categories Dashboard</span>
          </div>
          <Button
            trailingIcon={<FaPlus />}
            type="primary"
            onClick={async () => {
              const adCategoryId = searchParams.get("id");
              if (adCategoryId) {
                await getAdCategoryDetailData(adCategoryId);
                const detailData =
                  useAdCategoryStore.getState().adCategoryDetailData;
                if (detailData) {
                  setEditData(detailData);
                }
                navigate(`/create-ad-categories?id=${adCategoryId}&step=2`);
              }
            }}
          >
            Add New Publisher
          </Button>
        </div>

        <hr className="border-[#EA33C0] mb-2" />

        <div className="pt-2">
          <table className="w-full mt-5">
            <thead className="border-b border-gray-300 text-gray-600/50 text-sm">
              <tr>
                <th className="text-left py-2">
                  <div
                    className="flex items-center cursor-pointer gap-1 select-none"
                    onClick={() => handleSort("name")}
                  >
                    Publisher Name
                    <SortIcon field="name" />
                  </div>
                </th>
                <th className="text-left py-2">
                  <div
                    className="flex items-center cursor-pointer gap-1 select-none"
                    onClick={() => handleSort("status")}
                  >
                    Status
                    <SortIcon field="status" />
                  </div>
                </th>
                <th className="text-left py-2">
                  <div
                    className="flex items-center cursor-pointer gap-1 select-none"
                    onClick={() => handleSort("ad_type")}
                  >
                    Ad Type
                    <SortIcon field="ad_type" />
                  </div>
                </th>
                <th className="text-left py-2">
                  <div
                    className="flex items-center cursor-pointer gap-1 select-none"
                    onClick={() => handleSort("placement_info")}
                  >
                    Placement Info
                    <SortIcon field="placement_info" />
                  </div>
                </th>
                <th className="text-left py-2">
                  <div
                    className="flex items-center cursor-pointer gap-1 select-none"
                    onClick={() => handleSort("active_parameters")}
                  >
                    Active Parameters
                    <SortIcon field="active_parameters" />
                  </div>
                </th>
                <th className="text-left py-2">
                  <div
                    className="flex items-center cursor-pointer gap-1 select-none"
                    onClick={() => handleSort("inactive_parameters")}
                  >
                    Inactive Parameters
                    <SortIcon field="inactive_parameters" />
                  </div>
                </th>
                <th className="text-left py-2">
                  <div
                    className="flex items-center cursor-pointer gap-1 select-none"
                    onClick={() => handleSort("date_created")}
                  >
                    Date Created
                    <SortIcon field="date_created" />
                  </div>
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {currentRows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-6 text-center text-gray-500">
                    No Data Found
                  </td>
                </tr>
              ) : (
                currentRows.map((row, i) => (
                  <tr key={i} className="border-b border-gray-200">
                    <td className="py-4 font-semibold">{row.publisherName}</td>
                    <td className="py-4">Active</td>
                    <td className="py-4">{row.adInputName}</td>
                    <td className="py-4">{row.placementInfo}</td>
                    <td className="py-4">{row.activeParameters}</td>
                    <td className="py-4">{row.inactiveParameters}</td>
                    <td className="py-4">
                      {row.createdAt
                        ? new Date(row.createdAt).toLocaleDateString()
                        : "-"}
                    </td>
                    <td className="text-right">
                      <PopoverMenu
                        items={createMenuItems(
                          adCategoryDetailData?.id || "",
                          row.publisherId,
                          row.publisherName,
                          row.adInputName,
                          row.adInputId,
                        )}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end items-center mt-6 space-x-4 text-sm">
          <Button
            type="secondary"
            onClick={goPrevious}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <span>
            Page {currentPage} of {totalPages || 1}
          </span>
          <Button
            type="secondary"
            onClick={goNext}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Archive Publisher Confirmation Dialog */}
      <Dialog
        open={!!publisherToDelete}
        onOpenChange={() => setPublisherToDelete(null)}
      >
        <DialogContent className="sm:max-w-xl bg-white">
          <DialogHeader>
            <div className="font-semibold text-xl text-center">
              Archive Publisher
            </div>
            <DialogDescription className="mt-4 text-center text-gray-600">
              Are you sure you want to archive{" "}
              <span className="font-semibold">{publisherToDelete?.name}</span>?
              This action can be undone by re-activating the publisher.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-center gap-4 mt-6">
            <Button type="secondary" onClick={() => setPublisherToDelete(null)}>
              Cancel
            </Button>
            <Button
              type="primary"
              trailingIcon={<BiTrash fontSize={18} />}
              onClick={handleDelete}
            >
              Yes, Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Ad Type Confirmation Dialog */}
      <Dialog
        open={!!adTypeToDelete}
        onOpenChange={() => setAdTypeToDelete(null)}
      >
        <DialogContent className="sm:max-w-xl bg-white">
          <DialogHeader>
            <div className="font-semibold text-xl text-center">
              Archive Ad Type
            </div>
            <DialogDescription className="mt-4 text-center text-gray-600">
              Are you sure you want to archive the ad type{" "}
              <span className="font-semibold">{adTypeToDelete?.name}</span>?
              This action can be undone by re-activating the ad type.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-center gap-4 mt-6">
            <Button type="secondary" onClick={() => setAdTypeToDelete(null)}>
              Cancel
            </Button>
            <Button
              type="primary"
              trailingIcon={<BiTrash fontSize={18} />}
              onClick={handleDeleteAdType}
            >
              Yes, Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
